import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';

import {
  Button,
} from '@lockerpm/design';

import authComponents from "./components";
import itemsComponents from "../../components/items";
import formsComponents from "../../components/forms";
import setupPwlComponents from "./components/setup-pwl";
import lockComponents from "../../web-sh/src/pages/auth/components/lock";

import authServices from "../../services/auth";
import userServices from "../../services/user";
import common from "../../utils/common";
import global from "../../config/global";

const SetupPasswordless = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { AuthCard, GetMPHintModal } = authComponents;
  const { UserInfo } = itemsComponents;
  const { SecurityKey, Passkey, Pairing } = formsComponents;
  const { SetUpPwlTitle, SelectUnlockMethod } = setupPwlComponents;
  const { MPForm } = lockComponents;

  const userInfo = useSelector((state) => state.auth.userInfo);
  const query = common.convertStringToQuery(window.location.search);

  const [callingAPI, setCallingAPI] = useState(false);
  const [isPair, setIsPair] = useState(false);
  const [step, setStep] = useState(1);
  const [otherMethod, setOtherMethod] = useState(null);
  const [mpHintVisible, setMPHintVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState(null);

  const handleUnlock = async (values) => {
    setCallingAPI(true)
    const payload = {
      email: userInfo.email,
      username: userInfo.email,
      password: values.password,
      kdf: userInfo.kdf,
      kdf_iterations: userInfo.kdf_iterations,
      kdf_memory: userInfo.kdf_memory,
      kdf_parallelism: userInfo.kdf_parallelism
    }
    await common.unlockToVault(payload, {}, () => {}).then(() => {
      setCurrentPassword(values.password);
    }).catch((error) => {
      const { code } = common.getErrorMessage(error);
      if (code == 1012) {
        global.navigate(global.keys.SETUP_2FA)
      }
      global.pushError(error)
    });
    setCallingAPI(false);
  }

  const handleSetUpPWL = async (newPassword) => {
    setCallingAPI(true);
    await userServices.change_password({
      username: userInfo?.email,
      login_method: 'passwordless',
      password: currentPassword,
      kdf: userInfo?.kdf,
      kdf_iterations: userInfo?.kdf_iterations,
      kdf_memory: userInfo?.kdf_memory,
      kdf_parallelism: userInfo?.kdf_parallelism
    }, {
      password: newPassword,
      kdf: userInfo?.kdf,
      kdf_iterations: userInfo?.kdf_iterations,
      kdf_memory: userInfo?.kdf_memory,
      kdf_parallelism: userInfo?.kdf_parallelism
    })
    await common.fetchUserInfo();
    const returnUrl = query?.return_url ? decodeURIComponent(query?.return_url) : '/';
    const payload = {
      email: userInfo?.email,
      username: userInfo?.email,
      sync_all_platforms: userInfo?.sync_all_platforms,
      unlock_method: otherMethod,
      password: newPassword,
      kdf: userInfo?.kdf,
      kdf_iterations: userInfo?.kdf_iterations,
      kdf_memory: userInfo?.kdf_memory,
      kdf_parallelism: userInfo?.kdf_parallelism
    }
    await common.unlockToVault(payload, query, () => navigate(returnUrl, { replace: true })).then(() => {
      common.getResourceCountries();
      common.getResourcePlans();
      common.setIsUnlocked();
    }).catch((error) => {
      const { code } = common.getErrorMessage(error);
      if (code == 1012) {
        global.navigate(global.keys.SETUP_2FA)
      }
      global.pushError(error)
    });
    setCallingAPI(false);
  }

  return (
    <div
      className="lock-page auth-page"
    >
      <AuthCard
        className="w-[600px]"
        other={
          <div className="mt-4 text-center">
            <span>
              {t('setup_2fa.note')}
              <Button
                type="link"
                className="font-semibold"
                onClick={() => authServices.logout(true)}
              >
                {t('button.logout')}
              </Button>
            </span>
          </div>
        }
      >
        <>
          <SetUpPwlTitle
            step={step}
            callingAPI={callingAPI}
            setStep={setStep}
            setIsPair={setIsPair}
          />
          <div className="mt-4 mb-6 flex items-center justify-center">
            <UserInfo />
          </div>
          {
            !currentPassword &&  <MPForm
              logging={!userInfo?.email}
              callingAPI={callingAPI}
              isShowMPHint={true}
              setMPHintVisible={setMPHintVisible}
              onUnlock={handleUnlock}
            />
          }
          {
            !!currentPassword && <div>
              {
                isPair && <div>
                  <Pairing
                    userInfo={userInfo}
                    callingAPI={callingAPI}
                    onConfirm={() => setIsPair(false)}
                  />
                </div>
              }
              {
                !isPair && <div>
                  {
                    step === 1 && <SelectUnlockMethod
                      callingAPI={callingAPI}
                      setStep={setStep}
                      setIsPair={setIsPair}
                      setOtherMethod={setOtherMethod}
                    />
                  }
                  {
                    step === 2 && <div>
                      {
                        otherMethod === 'security_key' && <SecurityKey
                          changing={callingAPI}
                          isAddKey={true}
                          isNewKey={true}
                          userInfo={userInfo}
                          onRepair={() => setIsPair(true)}
                          onConfirm={(p) => handleSetUpPWL(p)}
                        />
                      }
                      {
                        otherMethod === 'passkey' && <Passkey
                          changing={callingAPI}
                          isAddKey={true}
                          isNewKey={true}
                          userInfo={userInfo}
                          onConfirm={(p) => handleSetUpPWL(p)}
                        />
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </>
      </AuthCard>
      <GetMPHintModal
        visible={mpHintVisible}
        userInfo={userInfo}
        onClose={() => setMPHintVisible(false)}
      />
    </div>
  );
}

export default SetupPasswordless;